import {
  OrderDetailModel,
  OrderFoodReportModel,
  OrderFoodReportSumModel,
  OrderListItemModel,
  OrderOperateStatusChangeModel,
  OrderPriorityChangeModel,
  OrderOutMutationResultModel,
  OrderOutListModel,
  OrderOutSelectListModel,
  OrderTableListItemModel,
  StoveDistributionModel,
} from '@/model/order';
import {
  OrderPaymentResultModel,
  OrderRefundRequestModel,
  OrderRefundResultModel,
} from '@/model/orderPayment';
import { OrderPickModel } from '@/model/orderPick';
import { OrderInvoiceModel, OrderInvoiceResultModel } from '@/model/orderInvoice';
import { ResultModel } from '@/model/viewModel';
import { ApiResult, post } from '@/utils/api';

// 新增订单返回结果
export interface AddOrderResultModel {
  orderId: number;
  orderOutIds: number[];
  tableOrderCompleteType: number;
}

export interface EditOrderTableRequestModel {
  orderId: number;
  tableId: number;
  dineType: number;
}

export interface EditOrderTableResultModel {
  orderId: number;
  orderOutIds: number[];
  tableOrderCompleteType: number;
}

export interface EditSeatNumberRequestModel {
  orderId: number;
  seatNumber: string;
}

/**
 * 卡券核销结果
 */
export interface CouponUseResultModel {
  couponName: string;
  couponNumber: string;
  couponCount: number;
}

/**
 * 卡券核销请求
 */
export interface CouponUseRequestModel {
  couponNumbers: string[];
  qrcode: string;
  shopFoodIds: number[];
  shopId: number;
}

export const getOrderList = (data: any): Promise<ApiResult<ResultModel<OrderListItemModel>>> =>
  post<ResultModel<OrderListItemModel>>('/order/select', data);

export const getOrderTableList = (
  data: any,
): Promise<ApiResult<ResultModel<OrderTableListItemModel>>> =>
  post<ResultModel<OrderTableListItemModel>>('/order/selectTable', data);

export const fetchAddOrder = (data: any): Promise<ApiResult<AddOrderResultModel>> =>
  post<AddOrderResultModel>('/order/insert', data);

export const fetchAddFoodWithOrder = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/food/insert', data);

export const getOrder = (data: any): Promise<ApiResult<OrderDetailModel>> =>
  post<OrderDetailModel>('/order/selectOne', data);

export const auditOrder = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/audit', data);

export const editOrderTable = (
  data: EditOrderTableRequestModel,
): Promise<ApiResult<EditOrderTableResultModel>> =>
  post<EditOrderTableResultModel>('/order/editTable', data);

export const editSeatNumber = (data: EditSeatNumberRequestModel): Promise<ApiResult<number>> =>
  post<number>('/order/editSeatNumber', data);

export const completeOrder = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/complete', data);

export const cancelOrderFood = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/food/cancel', data);

export const changeOperateStatus = (
  data: OrderOperateStatusChangeModel,
): Promise<ApiResult<OrderOutMutationResultModel>> =>
  post<OrderOutMutationResultModel>('/order/out/changeOperateStatus', data);

export const changePriority = (
  data: OrderPriorityChangeModel,
): Promise<ApiResult<OrderOutMutationResultModel>> =>
  post<OrderOutMutationResultModel>('/order/out/changePriority', data);

export const getOrderOutList = (data: any): Promise<ApiResult<OrderOutListModel>> =>
  post<OrderOutListModel>('/order/out/selectOrder', data);

export const distributeStove = (
  data: StoveDistributionModel,
): Promise<ApiResult<OrderOutMutationResultModel>> =>
  post<OrderOutMutationResultModel>('/order/out/distributeStove', data);

export const getOrderOutSelectList = (data: any): Promise<ApiResult<OrderOutSelectListModel>> =>
  post<OrderOutSelectListModel>('/order/out/select', data);

export const insertOrderPayment = (data: any): Promise<ApiResult<OrderPaymentResultModel>> =>
  post<OrderPaymentResultModel>('/order/payment/insert', data);

export const cancelOrder = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/cancel', data);

export const editAmount = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/editAmount', data);

export const printFood = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/out/print/food', data);

export const getOrderPick = (data: any): Promise<ApiResult<OrderPickModel>> =>
  post<OrderPickModel>('/order/selectPickCode', data);

/**
 * 打印收银小票
 * @param data 订单
 * @returns
 */
export const orderPrint = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/print/order', data);

/**
 * 打印订单小票
 * @param data 订单
 * @returns
 */
export const orderSlipPrint = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/print/orderSlip', data);

/**
 * 打印预定订单小票
 * @param data 订单
 * @returns
 */
export const bookOrderSlipPrint = (data: { orderId: number }): Promise<ApiResult<number>> =>
  post<number>('/order/print/bookOrderSlip', data);

/**
 * 打印外卖订单小票
 * @param data 订单
 * @returns
 */
export const deliveryOrderSlipPrint = (data: { orderId: number }): Promise<ApiResult<number>> =>
  post<number>('/order/print/deliveryOrderSlip', data);

/**
 * 打印菜品标签
 * @param data 订单
 * @returns
 */
export const orderFoodPrint = (data: any): Promise<ApiResult<number>> =>
  post<number>('/order/print/orderFood', data);

/**
 * 新增退款
 * @param data 退款
 * @returns 退款ID
 */
export const insertOrderRefund = (
  data: OrderRefundRequestModel,
): Promise<ApiResult<OrderRefundResultModel>> =>
  post<OrderRefundResultModel>('/order/refund/insert', data);

/**
 * 退款完成
 * @param data 退款
 * @returns 退款ID
 */
export const orderRefundComplete = (data: any): Promise<ApiResult<OrderRefundResultModel>> =>
  post<OrderRefundResultModel>('/order/refund/complete', data);

/**
 * 新增发票
 * @param data 发票
 * @returns 发票ID
 */
export const insertOrderInvoice = (
  data: OrderInvoiceModel,
): Promise<ApiResult<OrderInvoiceResultModel>> =>
  post<OrderInvoiceResultModel>('/order/invoice/insert', data);

/**
 * 查询订单餐品报表
 * @param data 查询条件（与订单列表类似：createtimeBegin/End, pageNum, pageSize, data: shopId/orderStatuss/orderNo/paymentStatuss 等）
 * @returns 分页结果，result.list 为 OrderFoodReportModel[]
 */
export const getOrderFoodReport = (
  data: any,
): Promise<ApiResult<ResultModel<OrderFoodReportModel>>> =>
  post<ResultModel<OrderFoodReportModel>>('/report/selectOrderFood', data);

/**
 * 查询订单餐品报表汇总
 * @param data 与订单餐品报表相同的查询条件
 * @returns 当前查询条件下的订单、菜品及金额汇总
 */
export const getOrderFoodReportSum = (data: any): Promise<ApiResult<OrderFoodReportSumModel>> =>
  post<OrderFoodReportSumModel>('/report/selectOrderFoodSum', data);

/**
 * 核销客户卡券
 * @param data 卡券核销参数
 * @returns 核销结果
 */
export const useCustomerCoupon = (
  data: CouponUseRequestModel,
): Promise<ApiResult<CouponUseResultModel>> =>
  post<CouponUseResultModel>('/customer/coupon/use', data);
